import {
  ContentExplorerEntry,
  ContentLibraryFileEntry,
  ContentLibraryFileProcessingMeta,
  ContentLibraryFolder,
  ContentLibrarySharedFile,
  ContentLibrarySharedFiles,
  ContentLibrarySharedFolder,
  FileType,
} from 'features/content-library/types';
import { isPrivateWhiteboard } from 'features/content-library/utils/isPrivateWhiteboard';
import { sortContentLibraryEntries } from 'features/content-library/utils/sortContentLibraryEntries';

export const transformFolderItem = (
  folder: ContentLibraryFolder,
  rootFolder: ContentLibraryFolder
): ContentExplorerEntry => ({
  id: folder.id,
  name: folder.name,
  isFolder: true,
  type: null,
  rootFolder: {
    id: rootFolder.id,
    name: rootFolder.name,
  },
  personal: folder.personal,
  shared: false,
  date: folder.date,
});

export const transformFileItem = (
  file: ContentLibraryFileEntry,
  rootFolder: ContentLibraryFolder
): ContentExplorerEntry => ({
  id: file.id,
  name: file.name,
  isFolder: false,
  type: file.type,
  rootFolder: {
    id: rootFolder.id,
    name: rootFolder.name,
  },
  personal: file.personal,
  shared: file.shared,
  private: file.private ?? false,
  date: file.date,
});

export const transformMetaItem = (
  meta: ContentLibraryFileProcessingMeta,
  rootFolder: ContentLibraryFolder
): ContentExplorerEntry => ({
  id: meta.id,
  name: meta.name,
  isFolder: false,
  type: null,
  rootFolder: {
    id: rootFolder.id,
    name: rootFolder.name,
  },
  personal: meta.personal,
  shared: false,
  date: '',
});

export const transformSharedFolderItem = (
  sharedFolder: ContentLibrarySharedFolder,
  rootFolder: ContentLibraryFolder
): ContentExplorerEntry => ({
  id: sharedFolder.id,
  name: sharedFolder.name,
  isFolder: true,
  type: null,
  rootFolder: {
    id: rootFolder.id,
    name: rootFolder.name,
  },
  personal: false,
  shared: false,
  date: '',
});

export const transformSharedFileItem = (
  sharedFile: ContentLibrarySharedFile,
  sharedFolder: ContentLibrarySharedFolder
): ContentExplorerEntry => ({
  id: sharedFile.id,
  name: sharedFile.name,
  isFolder: false,
  type: sharedFile.type,
  rootFolder: {
    id: sharedFolder.id,
    name: sharedFolder.name,
  },
  personal: false,
  shared: true,
  sharedBy: sharedFile.sharedBy,
  date: '',
});

const canDownloadSharedFile = (fileType: FileType) => {
  const unsupportedTypes: FileType[] = ['whiteboard', 'vimeo', 'youtube', 'webapp'];

  return !unsupportedTypes.includes(fileType);
};

/**
 * Transforms shared files into content explorer items, filtering out
 * files that cannot be opened and downloaded based on present library permission
 */
export const transformAndFilterSharedFiles = (
  sharedFilesData: ContentLibrarySharedFiles,
  canPresentLibrary: boolean,
  canManagePrivateWhiteboards: boolean
): ContentExplorerEntry[] => {
  const items: ContentExplorerEntry[] = [];

  for (const sharedFileId of sharedFilesData.folder.childFileIds) {
    const sharedFile = sharedFilesData.files[sharedFileId];
    if (sharedFile) {
      if (
        (!isPrivateWhiteboard(sharedFile) || canManagePrivateWhiteboards) &&
        (canPresentLibrary || canDownloadSharedFile(sharedFile.type))
      ) {
        items.push(transformSharedFileItem(sharedFile, sharedFilesData.folder));
      }
    }
  }

  return sortContentLibraryEntries(items, 'name');
};
